/**
 * recentVendors.js — Iter 67ad
 *
 * Pure localStorage-backed helper for the "Recently viewed hosts" UX. Each
 * record is `{id, name, logo, viewedAt}`. We dedupe by `id` (most recent
 * wins) and cap the list at 5 so the strip never grows unbounded.
 *
 * Why localStorage? It's the right tier for this kind of preference — no
 * round-trip to the backend, survives full-page reloads, scoped to the
 * device. We deliberately avoid syncing to the backend so the renter feels
 * a private "your last 5" without a "we're tracking you" vibe.
 */
const STORAGE_KEY = "rentalcar:recent-vendors";
const MAX_RECENT = 5;

function _safeParse(raw) {
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function getRecentVendors() {
  if (typeof window === "undefined" || !window.localStorage) return [];
  return _safeParse(window.localStorage.getItem(STORAGE_KEY) || "[]");
}

export function recordVendorVisit({ id, name, logo }) {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (!id) return;
  const existing = getRecentVendors().filter((v) => v && v.id !== id);
  const next = [
    { id, name: name || "Host", logo: logo || null, viewedAt: Date.now() },
    ...existing,
  ].slice(0, MAX_RECENT);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    // Same-tab listeners (the strip itself) need a manual nudge — the native
    // `storage` event only fires across tabs. We dispatch a custom event so
    // the `RecentVendorsStrip` re-renders immediately on click.
    window.dispatchEvent(new CustomEvent("rentalcar:recent-vendors-updated"));
  } catch {
    // Quota exceeded / private mode — silently ignore, the strip is best-effort.
  }
}

export function clearRecentVendors() {
  if (typeof window === "undefined" || !window.localStorage) return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent("rentalcar:recent-vendors-updated"));
}
